import React, { useState, useEffect, useCallback } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart2, Bell, BookOpen, Building, Bus, ChevronLeft, ChevronRight, Command, CreditCard, FileText, Filter, GraduationCap, Home, LayoutDashboard, LayoutGrid, Menu, Package, Plus, Search, Settings, Shield, ShoppingBag, Sparkles, UserCheck, Users, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { UserMenuDropdown } from "../components/auth/UserMenuDropdown";
import { SecurityIndicatorBar } from "../components/auth/SecurityIndicatorBar";
import { ThemeToggle } from "../components/public/ThemeToggle";
import { Breadcrumbs } from "../components/dashboard/Breadcrumbs";
import { CommandPalette } from "../components/dashboard/CommandPalette";
import { NotificationDrawer } from "../components/dashboard/NotificationDrawer";
import { DashboardSettings } from "../components/dashboard/DashboardSettings";
import { DashboardPersonalization, useDashboardPersonalization } from "../components/dashboard/DashboardPersonalization";
import { notificationApi } from "../services/notificationApi";

import { SIDEBAR_CONFIG } from "../config/sidebarConfig";

export interface NavGroup {
  groupLabel: string;
  items: {
    label: string;
    route: string;
    icon: React.ReactNode;
    badge?: string;
  }[];
}

const renderNavIcon = (iconName: string) => {
  switch (iconName) {
    case "LayoutDashboard":
      return <LayoutDashboard className="w-4 h-4 text-indigo-400" />;
    case "BookOpen":
      return <BookOpen className="w-4 h-4 text-indigo-400" />;
    case "BarChart2":
      return <BarChart2 className="w-4 h-4 text-emerald-400" />;
    case "FileText":
      return <FileText className="w-4 h-4 text-amber-400" />;
    case "Sparkles":
      return <Sparkles className="w-4 h-4 text-purple-400" />;
    case "UserCheck":
      return <UserCheck className="w-4 h-4 text-cyan-400" />;
    case "Shield":
      return <Shield className="w-4 h-4 text-blue-400" />;
    case "Users":
      return <Users className="w-4 h-4 text-sky-400" />;
    case "ShoppingBag":
      return <ShoppingBag className="w-4 h-4 text-blue-400" />;
    case "CreditCard":
      return <CreditCard className="w-4 h-4 text-emerald-400" />;
    case "Building":
      return <Building className="w-4 h-4 text-orange-400" />;
    case "Bus":
      return <Bus className="w-4 h-4 text-sky-400" />;
    case "Package":
      return <Package className="w-4 h-4 text-emerald-400" />;
    case "Settings":
      return <Settings className="w-4 h-4 text-slate-400" />;
    default:
      return <LayoutDashboard className="w-4 h-4 text-indigo-400" />;
  }
};

export const DashboardLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { tenant, user, hasRole, hasPermission } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Enterprise Panel States
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPersonalizeOpen, setIsPersonalizeOpen] = useState(false);
  const [density, setDensity] = useState<"comfortable" | "compact">(() => {
    return (localStorage.getItem("dashboard_density") as "comfortable" | "compact") || "comfortable";
  });

  const { widgets, toggleWidget, resetWidgets } = useDashboardPersonalization();

  // Global Ctrl+K shortcut
  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsCommandOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setIsCommandOpen(false);
        setIsNotifOpen(false);
        setIsSettingsOpen(false);
        setIsPersonalizeOpen(false);
      }
    },
    []
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  const [unreadNotifCount, setUnreadNotifCount] = useState<number>(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const summary = await notificationApi.getSummary();
      if (summary?.stats?.unread_count !== undefined) {
        setUnreadNotifCount(summary.stats.unread_count);
      } else {
        const inbox = await notificationApi.getInbox();
        setUnreadNotifCount(inbox.filter((n) => n.status === "unread").length);
      }
    } catch (err) {
      console.error("Failed to fetch unread notification count:", err);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount, isNotifOpen]);

  // Filter single central SIDEBAR_CONFIG by user's RBAC roles & permissions
  const menuGroups: NavGroup[] = SIDEBAR_CONFIG.map((group) => {
    const items = group.items
      .filter((item) => {
        if (item.isFinished === false) return false;
        if (item.roles && item.roles.length > 0) {
          if (!hasRole(item.roles)) return false;
        }
        if (item.permission) {
          if (!hasPermission(item.permission)) return false;
        }
        return true;
      })
      .map((item) => ({
        label: item.label,
        route: item.route,
        icon: renderNavIcon(item.iconName),
        badge: item.badge,
      }));

    return { groupLabel: group.groupLabel, items };
  }).filter((group) => group.items.length > 0);

  return (
    <div className={`min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-indigo-500 selection:text-white ${density === "compact" ? "text-xs" : ""}`}>
      {/* Security Status Bar */}
      <SecurityIndicatorBar />

      {/* Main Top Header */}
      <header
        className="h-16 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-30 px-4 sm:px-6 flex items-center justify-between gap-4"
        role="banner"
      >
        {/* Left Controls & Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white"
            aria-label="Toggle mobile navigation menu"
          >
            {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link to="/dashboard" className="flex items-center gap-2.5" aria-label="College ERP Dashboard Home">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 p-0.5 shadow-lg shadow-indigo-500/20">
              <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-indigo-400" />
              </div>
            </div>
            <div className="hidden md:flex flex-col">
              <span className="text-base font-extrabold tracking-tight text-white leading-tight">College ERP</span>
              <span className="text-[10px] text-indigo-400 font-mono font-semibold">Enterprise Portal</span>
            </div>
          </Link>

          {/* Academic Session & Tenant Badges */}
          <div className="hidden xl:flex items-center gap-2 pl-4 border-l border-slate-800">
            <span className="px-2.5 py-1 rounded-full bg-slate-900 border border-slate-800 text-[11px] font-mono font-bold text-slate-300">
              AY 2025-2026
            </span>
            <span className="px-2.5 py-1 rounded-full bg-indigo-950/80 border border-indigo-800 text-[11px] font-mono font-bold text-indigo-300">
              Tenant: {typeof tenant === "string" ? tenant : (tenant?.name || tenant?.slug || "college-main")}
            </span>
          </div>
        </div>

        {/* Search & Quick Action Center Ã¢â‚¬â€ opens Command Palette */}
        <div className="flex-1 max-w-md hidden sm:block">
          <button
            onClick={() => setIsCommandOpen(true)}
            className="w-full flex items-center gap-2 pl-3 pr-3 py-2 text-xs bg-slate-900/80 border border-slate-800/80 rounded-xl text-slate-400 hover:text-slate-200 hover:border-slate-700 transition-all text-left"
            aria-label="Open command palette search"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="flex-1">Search modules, students, reports... (Ctrl+K)</span>
            <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-slate-950 border border-slate-800 rounded text-slate-500">Ã¢Å’ËœK</kbd>
          </button>
        </div>

        {/* Right Action Icons */}
        <div className="flex items-center gap-2">
          {/* Quick Action */}
          <Link
            to="/students/create"
            className="p-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition-all shadow-md shadow-indigo-600/20 flex items-center gap-1"
            title="Quick Action: Enroll Student"
            aria-label="Quick action: enroll new student"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Quick Action</span>
          </Link>

          {/* Customize Dashboard */}
          <button
            onClick={() => setIsPersonalizeOpen(true)}
            className="relative p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors"
            aria-label="Customize dashboard widgets"
            title="Customize Dashboard"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>

          {/* Notifications */}
          <button
            onClick={() => setIsNotifOpen(true)}
            className="relative p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors"
            aria-label={`Open notification center, ${unreadNotifCount} unread notifications`}
            title="Notification Center"
          >
            <Bell className="w-4 h-4" />
            {unreadNotifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center text-[9px] font-mono font-bold bg-indigo-600 text-white rounded-full border border-slate-950">
                {unreadNotifCount}
              </span>
            )}
          </button>

          {/* Dashboard Settings */}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="relative p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors hidden sm:flex"
            aria-label="Open dashboard settings"
            title="Dashboard Settings"
          >
            <Settings className="w-4 h-4" />
          </button>

          <div className="hidden sm:block">
            <ThemeToggle />
          </div>

          <UserMenuDropdown />
        </div>
      </header>

      {/* Main Body Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <aside
          className={`hidden lg:flex flex-col border-r border-slate-900 bg-slate-950 transition-all duration-300 ${
            isSidebarCollapsed ? "w-20" : "w-64"
          }`}
          role="navigation"
          aria-label="Main navigation sidebar"
        >
          {/* Collapse Toggle */}
          <div className="p-3 border-b border-slate-900 flex justify-end">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
              aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          {/* Nav Items */}
          <div className="flex-1 overflow-y-auto p-3 space-y-6">
            {menuGroups.map((group, idx) => (
              <div key={idx} className="space-y-1">
                {!isSidebarCollapsed && (
                  <span className="px-3 text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider block">
                    {group.groupLabel}
                  </span>
                )}
                {group.items.map((item, itemIdx) => {
                  const isActive =
                    item.route === "/dashboard"
                      ? location.pathname === "/dashboard"
                      : location.pathname.startsWith(item.route);
                  return (
                    <Link
                      key={itemIdx}
                      to={item.route}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-xs font-semibold transition-all ${
                        isActive
                          ? "bg-indigo-600/10 text-indigo-400 border border-indigo-500/30 shadow-inner"
                          : "text-slate-400 hover:text-slate-100 hover:bg-slate-900"
                      }`}
                      title={item.label}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className={isActive ? "text-indigo-400" : "text-slate-400"}>{item.icon}</span>
                      {!isSidebarCollapsed && <span className="flex-1 truncate">{item.label}</span>}
                      {!isSidebarCollapsed && item.badge && (
                        <span className="px-2 py-0.5 text-[10px] font-mono font-bold rounded-full bg-slate-900 text-indigo-300 border border-slate-800">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Mobile Nav Drawer */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-md flex"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
            >
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 25 }}
                className="w-72 bg-slate-950 border-r border-slate-800 h-full p-4 overflow-y-auto space-y-6"
              >
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <span className="text-sm font-bold text-white">Navigation Menu</span>
                  <button onClick={() => setIsMobileOpen(false)} className="p-1 text-slate-400" aria-label="Close mobile menu">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {menuGroups.map((group, idx) => (
                    <div key={idx} className="space-y-1">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider block">
                        {group.groupLabel}
                      </span>
                      {group.items.map((item, itemIdx) => {
                        const isActive =
                          item.route === "/dashboard"
                            ? location.pathname === "/dashboard"
                            : location.pathname.startsWith(item.route);
                        return (
                          <Link
                            key={itemIdx}
                            to={item.route}
                            onClick={() => setIsMobileOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                              isActive
                                ? "bg-indigo-600/10 text-indigo-400 border border-indigo-500/30"
                                : "text-slate-300 hover:bg-slate-900 hover:text-white"
                            }`}
                          >
                            {item.icon}
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main
          className={`flex-1 overflow-y-auto space-y-6 ${density === "compact" ? "p-3 sm:p-4" : "p-4 sm:p-6 lg:p-8"}`}
          id="main-content"
          aria-label="Dashboard main content"
        >
          <Breadcrumbs />
          {children ?? <Outlet />}
        </main>
      </div>

      {/* Floating Command Palette Button */}
      <button
        onClick={() => setIsCommandOpen(true)}
        className="fixed bottom-6 right-6 p-3.5 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-2xl shadow-indigo-600/40 z-30 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Open command palette (Ctrl+K)"
        title="Command Palette (Ctrl+K)"
      >
        <Command className="w-5 h-5" />
      </button>

      {/* Enterprise Overlays */}
      <CommandPalette isOpen={isCommandOpen} onClose={() => setIsCommandOpen(false)} />
      <NotificationDrawer isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} />
      <DashboardSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        density={density}
        onDensityChange={setDensity}
      />
      <DashboardPersonalization
        isOpen={isPersonalizeOpen}
        onClose={() => setIsPersonalizeOpen(false)}
        widgets={widgets}
        onToggle={toggleWidget}
        onReset={resetWidgets}
      />
    </div>
  );
};


